import { getSonarObjectTypeLabelPlural } from '@sonar-software/sonar-ts-lib';
import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import { SelectionTable } from '@ui/tables';
import { AutomationSearch, AutomationSearchQueryParams, PeriscopeAutomation } from '.';
import { getRowKey, paginationDefaultQueryParams } from '..';
import {
	PeriscopeSfEntityAutomation,
	SfEntityAutomationType,
	SonarObjectChildTypeId,
	SonarObjectType,
	SonarObjectTypeId
} from '@sonar-software/sonar-ts-types';
import React, { useCallback, useRef } from 'react';
import { useBulkSelectWorkflow, useQueryResource, useSelectionTable } from '@hooks';

export type SalesforceAutomationSearchableSelectionTableQueryParams = AutomationSearchQueryParams & {
	type: SfEntityAutomationType;
};

export interface SalesforceAutomationSearchableSelectionTableProps {
	childSonarObjectType: SonarObjectChildTypeId;
	parentIndex: number;
	parentSonarObjectType: SonarObjectTypeId;
	sonarObjectId: number;
	showParents?: boolean;
	type: SfEntityAutomationType;
	uri: string;
}

export const automationToSonarObjectTypeMap: Record<SfEntityAutomationType, SonarObjectType | null> = {
	[ SfEntityAutomationType.EmailAlerts ]: SonarObjectType.SalesforceWorkflowAlert,
	[ SfEntityAutomationType.ApexClasses ]: SonarObjectType.SalesforceApexClass,
	[ SfEntityAutomationType.ApexTriggers ]: SonarObjectType.SalesforceApexTrigger,
	[ SfEntityAutomationType.EmailTemplate ]: SonarObjectType.SalesforceEmailTemplate,
	[ SfEntityAutomationType.FieldUpdates ]: SonarObjectType.SalesforceFieldUpdate,
	[ SfEntityAutomationType.Flows ]: SonarObjectType.SalesforceFlow,
	[ SfEntityAutomationType.FormulaFields ]: SonarObjectType.SalesforceField,
	[ SfEntityAutomationType.PageLayouts ]: SonarObjectType.SalesforcePageLayout,
	[ SfEntityAutomationType.ProcessBuilders ]: SonarObjectType.SalesforceProcessBuilder,
	[ SfEntityAutomationType.ValidationRules ]: SonarObjectType.SalesforceValidationRule,
	[ SfEntityAutomationType.Workflows ]: SonarObjectType.SalesforceWorkflowRule,
	[ SfEntityAutomationType.AllProcessAutomations ]: null
};

export const SalesforceAutomationSearchableSelectionTable: React.FC<
	SalesforceAutomationSearchableSelectionTableProps
> = ({ childSonarObjectType, parentIndex, parentSonarObjectType, sonarObjectId, type, uri }) => {
	const scrollableListRef = useRef<HTMLDivElement>(null);
	const sonarObjectType = automationToSonarObjectTypeMap[
		type || SfEntityAutomationType.AllProcessAutomations
	] as SonarObjectType;
	const selectionTypeLabel = type === SfEntityAutomationType.AllProcessAutomations?
		`${PERISCOPE_TAB_TYPES.PROCESS_AUTOMATION}s` :
		getSonarObjectTypeLabelPlural(sonarObjectType);

	const { loading, resource, queryParams, updateParams } = useQueryResource<
		PeriscopeSfEntityAutomation[],
		SalesforceAutomationSearchableSelectionTableQueryParams
	>({
		defaultParams: {
			...paginationDefaultQueryParams,
			hideInactive: true,
			type
		},
		uri
	});

	const { resourceIdsUri } = useBulkSelectWorkflow({
		childType: childSonarObjectType,
		parentType: parentSonarObjectType,
		sonarObjectId: sonarObjectId
	});

	const { type: automationType, ...queryParamsTypeRemoved } = queryParams;
	const selectionData = useSelectionTable({
		getRowKey,
		queryParams: queryParamsTypeRemoved,
		resourceIdsUri: resourceIdsUri,
		results: resource,
		selectionType: sonarObjectType
	});

	const renderAutomations = useCallback(
		(automation: PeriscopeSfEntityAutomation) =>
			<PeriscopeAutomation
				automation={ automation }
				parentIndex={ parentIndex }
				showType={ type === SfEntityAutomationType.AllProcessAutomations } />
		,
		[ parentIndex, type ]
	);

	return (
		<>
			<AutomationSearch
				onChange={ updateParams }
				queryParams={ queryParams } />
			<SelectionTable
				dataSource={ resource }
				loading={ loading }
				ref={ scrollableListRef }
				renderRow={ renderAutomations }
				rowKey={ getRowKey }
				selectionData={ selectionData }
				selectionType={ selectionTypeLabel } />
		</>
	);
};
